import { useClerk } from "@clerk/nextjs"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"

const actorSchema = z.object({ sub: z.string(), organization_impersonation: z.string().uuid() })
interface ImpersonationResponse {
  token: string
  guid: string
  clerk_organization: string | null
}

export function useOrganizationImpersonation() {
  const clerk = useClerk()
  const context = useApiContext()
  const queryClient = useQueryClient()
  const parsedActor = actorSchema.safeParse(clerk.session?.actor)
  const actor = parsedActor.success ? parsedActor.data : null

  function clearCachedIdentity() {
    queryClient.clear()
    localStorage.removeItem("slant-query-cache")
    localStorage.removeItem("slant:last-active-at")
    document.cookie = "dev_session_token=; path=/; max-age=0"
  }

  const start = useMutation({
    mutationFn: async ({ organizationId, userId }: { organizationId: string; userId: string }) => {
      if (!clerk.session || clerk.session.actor || !clerk.client)
        throw new Error("Sign in with your own account first.")
      const result = await apiContextFetch<ImpersonationResponse>(
        await context(),
        `organizations/${organizationId}/impersonations`,
        { method: "POST", body: JSON.stringify({ impersonation: { user_id: userId } }) }
      )
      // Complete the ticket handoff on Clerk's sign-in page so Next.js cannot refresh a half-signed-out page.
      const callback = new URL("/impersonation", window.location.origin)
      if (result.clerk_organization) callback.searchParams.set("organization", result.clerk_organization)
      const signInUrl = new URL(clerk.buildSignInUrl({ redirectUrl: callback.toString() }))
      signInUrl.searchParams.set("__clerk_ticket", result.token)
      clearCachedIdentity()
      await clerk.signOut({ redirectUrl: signInUrl.toString() })
    },
  })

  const stop = useMutation({
    mutationFn: async () => {
      if (!actor || !clerk.session || !clerk.client) throw new Error("No active organization impersonation.")
      try {
        await apiContextFetch(await context(), `organization_impersonations/${actor.organization_impersonation}`, {
          method: "DELETE",
        })
      } finally {
        clearCachedIdentity()
        await clerk.signOut({ redirectUrl: clerk.buildSignInUrl({ redirectUrl: "/organizations" }) })
      }
    },
  })

  return {
    start,
    stop,
    isImpersonating: !!actor,
    userName: clerk.user?.fullName || clerk.user?.primaryEmailAddress?.emailAddress,
  }
}
